import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Button,
  Typography,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Avatar,
  IconButton,
  Autocomplete,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import DocumentIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HubIcon from '@mui/icons-material/Hub';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmsIcon from '@mui/icons-material/Sms';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getAssociates, updateAssociate, getAssociateLedger, resendWelcomeSms } from '../../services/associate';
import { viewUrl } from '../../services/s3';
import PDFDownloadFunctionality from './PDFDownloadFunctionality';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';
import { exportAsExcel, exportAsCSV, exportAsPDF } from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { hasReadPermission, hasWritePermission } from '../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';

function normalizeAssociate(item) {
  const sponsor =
    Array.isArray(item.sponsorDetail) && item.sponsorDetail[0]
      ? item.sponsorDetail[0]
      : item.sponsor || item.sponsorInfo || item.sponsorDetails || item.sponsorData || {};
  const reg =
    Array.isArray(item.registrationDetail) && item.registrationDetail[0]
      ? item.registrationDetail[0]
      : {};
  const personal =
    Array.isArray(item.personalDetail) && item.personalDetail[0]
      ? item.personalDetail[0]
      : {};
  const bank =
    Array.isArray(item.bankDetail) && item.bankDetail[0]
      ? item.bankDetail[0]
      : {};
  const other =
    Array.isArray(item.otherDetail) && item.otherDetail[0]
      ? item.otherDetail[0]
      : {};
  const nominee =
    Array.isArray(item.nomineeDetail) && item.nomineeDetail[0]
      ? item.nomineeDetail[0]
      : {};

  const personalBusiness = item.personalBusiness ?? 0;
  const extraSelfBusiness = item.extraSelfBusiness ?? 0;

  return {
    _id: item._id || '',
    associatedId: String(item.associateId ?? item._id ?? ''),
    name: item.name ?? '',
    email: item.email ?? '',
    mobile: item.phone ?? item.whatsapp ?? '',
    role: item.userAccess?.role ?? item.role ?? '',
    loginId: item.userAccess?.loginId ?? item.loginId ?? '',
    designation: item.designation ?? '',
    city: item.city ?? '',
    state: item.state ?? '',
    image: item.img ?? '',
    active: !!item.isActive,
    status: item.isActive ? 'Active' : 'Inactive',
    updatedDate: item.updatedAt ?? '',
    pan: personal.pan ?? '',
    panImageViewUrl: personal.panImageViewUrl ?? '',
    aadhar: personal.aadhar ?? personal.addressNumber ?? '',
    addressImageViewUrl: personal.addressImageViewUrl ?? '',
    joiningDate: reg.registrationDate ?? '',
    rank: item.rank || '—',
    commission: item.commission ?? '',
    bankName: bank.bank ?? bank.bankName ?? '',
    accountNumber: bank.accountNumber ?? '',
    ifscCode: bank.ifsc ?? bank.ifscCode ?? '',
    branch: bank.branch ?? '',
    bankDocFile:
      bank.bankDocFileViewUrl ??
      bank.bankDocFileUrl ??
      bank.bankDocFile ??
      item.bankDocFileViewUrl ??
      item.bankDocFile ??
      '',
    bankDocFileUrl:
      bank.bankDocFileUrl ??
      bank.bankDocFile ??
      item.bankDocFile ??
      '',
    bankDocFileViewUrl:
      bank.bankDocFileViewUrl ??
      item.bankDocFileViewUrl ??
      '',
    address: item.address ?? '',
    sponsorName: sponsor.sponsorName ?? sponsor.name ?? item.sponsorName ?? item.sponsor?.name ?? '',
    sponsorCommission: sponsor.sponsorCommission ?? sponsor.commission ?? item.sponsorCommission ?? item.sponsor?.commission ?? '',
    sponsorEmail: sponsor.sponsorEmail ?? sponsor.email ?? item.sponsorEmail ?? item.sponsor?.email ?? '',
    sponsorMobile:
      sponsor.sponsorMobile ??
      sponsor.mobile ??
      sponsor.phone ??
      item.sponsorMobile ??
      item.sponsor?.mobile ??
      item.sponsor?.phone ??
      '',
    sponsorId:
      sponsor.sponsorId ??
      sponsor.associateId ??
      item.sponsorInfo?.sponsorId ??
      item.sponsorInfo?.associateId ??
      item.sponsorId ??
      item.sponsorBy ??
      '',
    sponsorLoginId:
      sponsor.sponsorLoginId ??
      item.sponsorInfo?.sponsorLoginId ??
      '',
    fatherName: personal.fatherName ?? '',
    dob: personal.dob ?? '',
    anniversary: personal.anniversary ?? '',
    qualification: personal.qualification ?? '',
    employeeType: other.employeeType ?? '',
    department: other.department ?? '',
    trialLastDate: other.trialLastDate ?? '',
    nomineeName: nominee.name ?? '',
    nomineePhone: nominee.phone ?? '',
    nomineeRelation: nominee.relation ?? '',
    loginId: item.userAccess?.loginId ?? '',
    userRole: item.userAccess?.role ?? '',
    userModules: item.userAccess?.modules ?? [],
    createdBy: item.createdBy ?? '',
    createdAt: item.createdAt ?? '',
    totalBookedAmount: item.totalBookedAmount ?? '',
    extraAmount: item.extraAmount ?? '',
    // totalBusiness: (item.totalBookedAmount ?? 0) - (item.extraAmount ?? 0),
    totalBusiness: item.totalBookedAmount ?? 0,
    personalBusiness: personalBusiness,
    // selfBusiness: personalBusiness - extraSelfBusiness,
    selfBusiness: personalBusiness,
    _raw: item,
  };
}


// Consolidated associate fetching into the main component to prevent redundant calls


async function softDeleteAssociate(id) {
  try {
    const body = await updateAssociate(id, { isDeleted: true });
    if (!body.success && !body.status) throw new Error(body.message || 'Delete failed');
    return body;
  } catch (e) {
    console.error('Soft Delete Error:', e);
    throw e;
  }
}

async function toggleAssociateStatus(id, isActive) {
  try {
    const body = await updateAssociate(id, { isActive });
    if (!body.success && !body.status) throw new Error(body.message || 'Status update failed');
    return body;
  } catch (e) {
    console.error('Status update error:', e);
    throw e;
  }
}

const ImagePreview = ({ src, alt }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchUrl = async () => {
      try {
        setLoading(true);
        if (!src) {
          setLoading(false);
          return;
        }
        // If it's already a full URL, try to use it, but viewUrl handles signing if needed based on key pattern
        const resolved = await viewUrl(src);
        if (active) {
          setUrl(resolved);
          setLoading(false);
        }
      } catch (e) {
        if (active) {
          console.error("Failed to load image preview for:", src, e);
          setError(true);
          setLoading(false);
        }
      }
    };
    fetchUrl();
    return () => { active = false; };
  }, [src]);

  if (loading) return <CircularProgress size={20} />;
  if (error || !url) return <Typography variant="caption" color="text.secondary">Preview unavailable</Typography>;

  return (
    <Box
      component="img"
      src={url}
      alt={alt}
      sx={{
        maxWidth: '100px',
        maxHeight: '100px',
        objectFit: 'contain',
        borderRadius: 1,
        border: '1px solid #eee',
        cursor: 'pointer'
      }}
      onClick={() => window.open(url, '_blank')}
    />
  );
};

const S3Avatar = ({ src, alt, sx, children, ...props }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!src) {
      setLoading(false);
      return;
    }
    const fetchUrl = async () => {
      try {
        const resolved = await viewUrl(src);
        if (active) {
          setUrl(resolved);
          setLoading(false);
        }
      } catch (e) {
        if (active) setLoading(false);
      }
    };
    fetchUrl();
    return () => { active = false; };
  }, [src]);

  return (
    <Avatar src={url || undefined} alt={alt} sx={sx} {...props}>
      {!url && !loading && (children || <PeopleIcon sx={{ color: '#2196F3', fontSize: sx?.width ? sx.width * 0.5 : 40 }} />)}
      {loading && <CircularProgress size={20} />}
    </Avatar>
  );
};

// Reset Button Component
const ResetButton = ({ onClick, disabled }) => (
  <Button
    variant="outlined"
    color="error"
    startIcon={<RefreshIcon />}
    onClick={onClick}
    disabled={disabled}
    fullWidth
    sx={{
      height: '56px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '12px',
      borderRadius: 2,
      borderColor: '#d32f2f',
      '&:hover': {
        borderColor: '#b71c1c',
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
      },
    }}
  >
    Reset
  </Button>
);

const AssociateList = () => {
  const history = useHistory();

  const canReadAssociate = hasReadPermission('associate.associate-list') || hasReadPermission('associate') || hasReadPermission('associate-list');
  const canWriteAssociate = hasWritePermission('associate.associate-list') || hasWritePermission('associate') || hasWritePermission('associate-list');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [cardTabValue, setCardTabValue] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [idCardDialogOpen, setIdCardDialogOpen] = useState(false);
  const [allAssociates, setAllAssociates] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState('');

  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const [smsLoading, setSmsLoading] = useState({});
  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };
  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    type: 'info',
    action: null
  });

  const searchTimeoutRef = useRef(null);
  const lastFetchParamsRef = useRef('');
  const lastFetchTimeRef = useRef(0);

  const [associatesState, setAssociatesState] = useState({
    data: [],
    totalDocs: 0,
    totalPages: 0,
    totalAssoicatelist: 0,
    totalActiveCount: 0,
    totalInactiveCount: 0,
    loading: false,
    error: '',
  });

  const fetchAssociates = useCallback(async () => {
    const paramsKey = JSON.stringify({ page, rowsPerPage, search, tabValue });
    const now = Date.now();

    // Guard: ignore if parameters are identical AND it's been less than 200ms since last fetch
    if (lastFetchParamsRef.current === paramsKey && (now - lastFetchTimeRef.current < 200)) {
      return;
    }

    lastFetchParamsRef.current = paramsKey;
    lastFetchTimeRef.current = now;

    setAssociatesState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (search) {
        // If search looks like "Name — Mobile", extract only the name for the API filterKey
        let filterValue = search;
        if (typeof search === 'string' && search.includes(' — ')) {
          filterValue = search.split(' — ')[0];
        }
        params.filterKey = filterValue;
      }
      if (tabValue === 1) params.isActive = true;
      if (tabValue === 2) params.isActive = false;

      const json = await getAssociates(params);
      if (!json.status || !json.data) throw new Error('Invalid response');

      const list = Array.isArray(json.data)
        ? json.data.map(normalizeAssociate)
        : [];

      const totalDocs = json.extra?.total ? parseInt(json.extra.total, 10) : list.length;
      const totalPages = totalDocs > 0 ? Math.ceil(totalDocs / rowsPerPage) : 1;
      const totalAssoicatelist = json.extra?.totalAssoicatelist || 0;
      const totalActiveCount = json.extra?.totalActiveCount || 0;
      const totalInactiveCount = json.extra?.totalInactiveCount || 0;

      setAssociatesState({
        data: list,
        totalDocs: totalDocs,
        totalPages: totalPages,
        totalAssoicatelist,
        totalActiveCount,
        totalInactiveCount,
        loading: false,
        error: '',
      });
    } catch (e) {
      console.error('Fetch Error:', e);
      setAssociatesState((s) => ({
        ...s,
        loading: false,
        error: e.message || 'Failed to load associates',
      }));
    }
  }, [page, rowsPerPage, search, tabValue]);

  // Main fetch effect - single source of truth
  useEffect(() => {
    fetchAssociates();
  }, [fetchAssociates]);

  const {
    data,
    totalDocs,
    loading,
    error,
    totalAssoicatelist,
    totalActiveCount,
    totalInactiveCount
  } = associatesState;

  const refetch = useCallback(() => {
    lastFetchParamsRef.current = ''; // Reset guard to allow manual refresh
    fetchAssociates();
  }, [fetchAssociates]);

  const formatValue = useCallback((value, placeholder = 'N/A') => {
    if (value === null || value === undefined) return placeholder;
    if (typeof value === 'string' && value.trim() === '') return placeholder;
    return value;
  }, []);

  const formatDateOnly = useCallback((v, placeholder = 'N/A') => {
    if (!v) return placeholder;
    const dateOnlyMatch = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
    let d = dateOnlyMatch ? new Date(v + 'T00:00:00') : new Date(v);
    if (Number.isNaN(d.getTime())) return placeholder;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  }, []);

  const formatDateTime = useCallback((v, placeholder = 'N/A') => {
    if (!v) return placeholder;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return placeholder;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  }, []);

  const hasValues = useCallback((values = []) => {
    return values.some((value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '' && value.trim().toLowerCase() !== 'n/a';
      return true;
    });
  }, []);

  useEffect(() => {
    // Only fetch suggestions if search input is focused and has value and we don't have names list yet
    if (searchInputValue.trim().length > 0 && allAssociates.length === 0) {
      const fetchAllAssociates = async () => {
        try {
          const params = { page: 1, limit: 10 };
          const json = await getAssociates(params);
          if (json.status && Array.isArray(json.data)) {
            const list = json.data.map(normalizeAssociate);
            setAllAssociates(list);
          }
        } catch (e) {
          console.error('Failed to fetch associates for suggestions:', e);
        }
      };
      fetchAllAssociates();
    }
  }, [searchInputValue, allAssociates.length]);

  const suggestions = useMemo(() => {
    if (!searchInputValue || searchInputValue.length < 1) return [];
    const searchLower = searchInputValue.toLowerCase();
    return allAssociates
      .filter(
        (associate) =>
          associate.name?.toLowerCase().includes(searchLower) ||
          associate.mobile?.includes(searchInputValue) ||
          associate.email?.toLowerCase().includes(searchLower) ||
          associate.associatedId?.includes(searchInputValue)
      )
      .slice(0, 10);
  }, [allAssociates, searchInputValue]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleSearchChange = useCallback((e, newValue) => {
    if (typeof newValue === 'string') {
      setSearch(newValue);
      setSearchInputValue(newValue);
      setPage(0);
    } else if (newValue && newValue.name) {
      setSearch(newValue.name);
      const displayValue = newValue.mobile
        ? `${newValue.name} — ${newValue.mobile}`
        : newValue.name;
      setSearchInputValue(displayValue);
      setPage(0);
    }
  }, []);

  const handleSearchInputChange = useCallback((e, newInputValue, reason) => {
    setSearchInputValue(newInputValue || '');

    // Only trigger API search if user is explicitly typing or clearing the input.
    // This prevents triggering a search for the formatted "Name — Mobile" string 
    // when an option is selected (reason === 'reset').
    if (reason === 'input' || reason === 'clear') {
      // Clear existing timeout
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      // Debounce the actual search fetch
      searchTimeoutRef.current = setTimeout(() => {
        setSearch(newInputValue || '');
        setPage(0);
      }, 500); // 500ms debounce
    }
  }, []);

  const handleReset = useCallback(() => {
    setSearch('');
    setSearchInputValue('');
    setTabValue(0);
    setPage(0);
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleCardTabChange = (associateId, newValue) =>
    setCardTabValue((prev) => ({ ...prev, [associateId]: newValue }));

  const getCardTabValue = (associateId) =>
    Object.prototype.hasOwnProperty.call(cardTabValue, associateId)
      ? cardTabValue[associateId]
      : 0;

  const handleEdit = (row) => {
    history.push({
      pathname: '/app/user/AssociateRegistration',
      state: { associate: row._raw || row },
    });
  };

  const handleDelete = (row) => {
    if (!row?._id) return;
    setConfirmDialog({
      open: true,
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete associate: ${row.name}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'error',
      action: async () => {
        try {
          const res = await softDeleteAssociate(row._id);
          const ok = res?.success === true || res?.status === true || res === true;
          if (ok) {
            showAlert(res?.message || 'Associate deleted successfully!', 'Success');
            await refetch();
          } else {
            throw new Error(res?.message || 'Delete operation returned failure status.');
          }
        } catch (e) {
          console.error('Delete Exception:', e);
          showAlert(e.message || 'Delete failed', 'Error');
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const handleToggleStatus = (row) => {
    if (!canWriteAssociate) {
      showAlert('You do not have permission to change associate status.', 'Warning');
      return;
    }
    if (!row?._id) return;
    const newStatus = row.status === 'Active' ? false : true;
    const statusText = newStatus ? 'Active' : 'Inactive';
    setConfirmDialog({
      open: true,
      title: 'Confirm Status Change',
      message: `Change status to ${statusText}?`,
      confirmText: statusText,
      cancelText: 'Cancel',
      type: newStatus ? 'success' : 'warning',
      action: async () => {
        try {
          await toggleAssociateStatus(row._id, newStatus);
          await refetch();
          showAlert(`Status updated to ${statusText}`, 'Success');
        } catch (e) {
          console.error('Status toggle failed:', e);
          showAlert(e.message || 'Status update failed', 'Error');
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const handleViewDetails = (associate) => {
    setSelectedAssociate(associate);
    setDetailsDialogOpen(true);
  };

  const handleIdCardClick = (associate) => {
    setSelectedRow(associate);
    setIdCardDialogOpen(true);
  };

  const handleIdCardDialogClose = () => {
    setIdCardDialogOpen(false);
    setSelectedRow(null);
  };

  const handleViewHistory = (associate) => {
    history.push(`/app/user/AssociateTransactionHistory/${associate._id}`);
  };

  const handleSendSms = (associate) => {
    if (!associate._id) return;
    setConfirmDialog({
      open: true,
      title: 'Send Welcome SMS',
      message: `Send a welcome SMS to ${associate.name} (${associate.mobile})?`,
      confirmText: 'Send SMS',
      cancelText: 'Cancel',
      type: 'info',
      action: async () => {
        setSmsLoading((prev) => ({ ...prev, [associate._id]: true }));
        try {
          const res = await resendWelcomeSms(associate._id);
          showAlert(res?.message || 'Welcome SMS sent successfully!', 'Success');
        } catch (e) {
          showAlert(e.message || 'Failed to send SMS', 'Error');
        } finally {
          setSmsLoading((prev) => ({ ...prev, [associate._id]: false }));
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
    });
  };

  // Handle old notification close or remove it
  const handleCloseNotification = (event, reason) => {
    // left intentionally empty or log
  };

  const buildExportRows = (list) => {
    return list.map((item) => ({
      'Associate ID': item.associatedId,
      'Name': item.name,
      'Email': item.email,
      'Mobile': item.mobile,
      'Designation': item.designation,
      'Role': item.role,
      // 'Rank': item.rank,
      'Commission (%)': item.commission,
      'City': item.city,
      'State': item.state,
      'Joining Date': formatDateOnly(item.joiningDate),
      'Status': item.status,
      'Sponsor Name': item.sponsorName,
      'Sponsor Login ID': item.sponsorLoginId || item.sponsorId,
      'Aadhar': item.aadhar,
      'PAN': item.pan,
      'Father Name': item.fatherName,
      'Self Business': item.selfBusiness,
      'Total Business': item.totalBusiness,
    }));
  };

  const handleExportPdf = () => {
    const rows = buildExportRows(data);
    if (!rows.length) {
      showAlert('No associate data available on this page to export.');
      return;
    }

    // Set specific widths for columns to optimize space on the PDF page
    const columnStyles = {
      0: { cellWidth: 35 }, // Associate ID
      3: { cellWidth: 60 }, // Mobile
      6: { cellWidth: 30 }, // Rank
      7: { cellWidth: 30 }, // Commission (%)
      11: { cellWidth: 35 }, // Status
      12: { cellWidth: 55 }, // Sponsor Name - reduced per user request
      13: { cellWidth: 40 }, // Sponsor ID - reduced per user request
      14: { cellWidth: 65 }, // Aadhar
      15: { cellWidth: 60 }, // PAN
    };

    exportAsPDF({
      rows,
      title: 'Associate List',
      fileNameBase: 'Associates_Export',
      columnStyles
    });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows(data);
    if (!rows.length) {
      showAlert('No associate data available on this page to export.');
      return;
    }
    exportAsCSV({ rows, fileNameBase: 'Associates_Export' });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows(data);
    if (!rows.length) {
      showAlert('No associate data available on this page to export.');
      return;
    }
    exportAsExcel({ rows, sheetName: 'Associates', fileNameBase: 'Associates_Export' });
  };

  if (!canReadAssociate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Associate List. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: '#f5f5f5',
        minHeight: '100vh',
        '& *': {
          transition: 'none !important',
          animation: 'none !important',
          '&::before, &::after': {
            transition: 'none !important',
            animation: 'none !important',
          },
        },
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}
            >
              Associate Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all associate registrations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {canWriteAssociate && (
              <ExportButton
                onExportPdf={handleExportPdf}
                onExportCsv={handleExportCsv}
                onExportExcel={handleExportExcel}
                variant="outlined"
                sx={{
                  px: 2.5,
                  py: 1.2,
                  fontSize: '15px'
                }}
              />
            )}
             {canWriteAssociate && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => history.push('/app/users/AssociateList/bulk-import')}
                startIcon={<CloudUploadIcon />}
                disableRipple
                sx={{
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '15px',
                  borderRadius: 2,
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  '&:hover': {
                    borderColor: '#536DFE',
                    backgroundColor: 'rgba(83, 109, 254, 0.04)',
                  },
                }}
              >
                Import
              </Button>
            )}
            {canWriteAssociate && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push('/app/user/AssociateRegistration')}
                startIcon={<PersonAddIcon />}
                disableRipple
                sx={{
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2,
                }}
              >
                Add New Associate
              </Button>
            )}
            <RefreshButton
              onClick={refetch}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 44 }
              }}
            />
           
          </Box>
        </Box>
      </Box>

      {/* Details Modal */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedAssociate?.name || 'Associate Details'} ({selectedAssociate?.associatedId || selectedAssociate?._id || '—'})
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const raw = selectedAssociate?._raw || {};
            const sponsor = Array.isArray(raw.sponsorDetail) && raw.sponsorDetail[0] ? raw.sponsorDetail[0] : {};
            const sponsorInfo = raw.sponsorInfo || {};
            const reg = Array.isArray(raw.registrationDetail) && raw.registrationDetail[0] ? raw.registrationDetail[0] : {};
            const personal = Array.isArray(raw.personalDetail) && raw.personalDetail[0] ? raw.personalDetail[0] : {};
            const bank = Array.isArray(raw.bankDetail) && raw.bankDetail[0] ? raw.bankDetail[0] : {};
            const other = Array.isArray(raw.otherDetail) && raw.otherDetail[0] ? raw.otherDetail[0] : {};
            const nominee = Array.isArray(raw.nomineeDetail) && raw.nomineeDetail[0] ? raw.nomineeDetail[0] : {};
            const userAccess = raw.userAccess || {};
            const modules = Array.isArray(userAccess.modules) ? userAccess.modules : [];
            const bankDocSource =
              bank.bankDocFileViewUrl ||
              raw.bankDocFileViewUrl ||
              bank.bankDocFileUrl ||
              bank.bankDocFile ||
              raw.bankDocFile ||
              '';

            const Item = ({ label, value }) => (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatValue(value)}
                </Typography>
              </Grid>
            );

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Photo */}
                <Box>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <S3Avatar
                        src={raw.img || ''}
                        alt={raw.name}
                        sx={{ width: 72, height: 72, border: '1px solid #e5e7eb' }}
                      />
                    </Grid>
                    <Grid item xs={12} md={10}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatValue(raw.name)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatValue(raw.designation)} • {formatValue(raw.role)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Basic Information</Typography>
                  <Grid container spacing={2}>
                    <Item label="Name" value={raw.name} />
                    {/* <Item label="Role" value={raw.role} /> */}
                    <Item label="Designation" value={raw.designation} />
                    <Item label="Status" value={raw.isActive ? 'Active' : 'Inactive'} />
                    <Item label="Associate ID" value={userAccess.loginId || raw._id} />
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">Updated At</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Paper variant="outlined" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateTime(raw.updatedAt)}
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">Created At</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Paper variant="outlined" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateTime(raw.createdAt)}
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                    <Item label="Created By" value={raw.createdBy} />
                    <Item label="Commission (%)" value={raw.commission} />
                    {raw.selfBusiness > 0 && <Item label="Self Business" value={`₹${raw.selfBusiness.toLocaleString('en-IN')}`} />}
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Contact</Typography>
                  <Grid container spacing={2}>
                    <Item label="Email" value={raw.email} />
                    <Item label="Phone" value={raw.phone} />
                    <Item label="WhatsApp" value={raw.whatsapp} />
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Location</Typography>
                  <Grid container spacing={2}>
                    <Item label="City" value={raw.city} />
                    <Item label="State" value={raw.state} />
                    <Item label="Address" value={raw.address} />
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Sponsor Details</Typography>
                  <Grid container spacing={2}>
                    <Item label="Sponsor Login ID" value={sponsorInfo.sponsorLoginId || raw.sponsorLoginId || (sponsorInfo.associateId || sponsor.associateId || sponsor.sponsorId)} />
                    {sponsor.sponsorName && <Item label="Sponsor Name" value={sponsor.sponsorName} />}
                    {sponsor.designation && <Item label="Designation" value={sponsor.designation} />}
                    {sponsor.sponsorEmail && <Item label="Sponsor Email" value={sponsor.sponsorEmail} />}
                    {sponsor.sponsorMobile && <Item label="Sponsor Mobile" value={sponsor.sponsorMobile} />}
                    {sponsor.idCard && <Item label="ID Card" value={sponsor.idCard} />}
                    {(!sponsor.sponsorName && !sponsor.sponsorEmail && !sponsor.sponsorMobile && !sponsor.idCard) && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Detailed sponsor information not available in this view.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Registration</Typography>
                  <Grid container spacing={2}>
                    <Item label="Registration Date" value={formatDateOnly(reg.registrationDate)} />
                    <Item label="Deactivation Date" value={formatDateOnly(reg.deActivationDate)} />
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Other Detail</Typography>
                  <Grid container spacing={2}>
                    <Item label="Employee Type" value={other.employeeType} />
                    <Item label="Department" value={other.department} />
                    <Item label="Trial Last Date" value={formatDateOnly(other.trialLastDate)} />
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Personal</Typography>
                  <Grid container spacing={2}>
                    <Item label="Father Name" value={personal.fatherName} />
                    <Item label="DOB" value={formatDateOnly(personal.dob)} />
                    <Item label="Anniversary" value={formatDateOnly(personal.anniversary)} />
                    <Item label="Qualification" value={personal.qualification} />
                    <Item label="PAN" value={personal.pan} />
                    <Item label="Aadhar/Address Number" value={personal.aadhar || personal.addressNumber} />
                    {personal.AddressType && <Item label="Address Type" value={personal.AddressType} />}
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Bank Details</Typography>
                  <Grid container spacing={2}>
                    <Item label="Account Name" value={bank.accountName} />
                    {/* <Item label="Account Holder Name" value={bank.accountHolderName} /> */}
                    <Item label="Bank Name" value={bank.bankName || bank.bank} />
                    <Item label="Account Number" value={bank.accountNumber} />
                    <Item label="IFSC" value={bank.ifscCode || bank.ifsc} />
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Nominee</Typography>
                  <Grid container spacing={2}>
                    <Item label="Name" value={nominee.name} />
                    <Item label="Phone" value={nominee.phone} />
                    <Item label="Relation" value={nominee.relation} />
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Documents</Typography>
                  <Grid container spacing={2}>
                    {/* Profile Photo */}
                    {/* <Grid item xs={12} sm={6}> */}
                    {/* <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}> */}
                    {/* <Box>
                          {raw.img ? (
                            <ImagePreview src={raw.imgUrl || raw.img} alt="Profile Photo" />
                          ) : (
                            <Avatar sx={{ bgcolor: '#e3f2fd', width: 56, height: 56 }}>
                              <ImageIcon sx={{ color: '#1976d2' }} />
                            </Avatar>
                          )}
                        </Box> */}
                    {/* <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Profile Photo</Typography>
                          {raw.img ? (
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={async () => {
                                const url = await viewUrl(raw.img);
                                if (url) window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', p: 0 }}
                            >
                              View Full Size
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Not Uploaded</Typography>
                          )}
                        </Box> */}
                    {/* </Box> */}
                    {/* </Grid> */}

                    {/* Address Proof */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                          {personal.addressImage ? (
                            <ImagePreview src={personal.addressImageViewUrl || personal.addressImage} alt="Address Proof" />
                          ) : (
                            <Avatar sx={{ bgcolor: '#f1f8f4', width: 56, height: 56 }}>
                              <DocumentIcon sx={{ color: '#2e7d32' }} />
                            </Avatar>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Address Proof ({personal.AddressType || 'N/A'})</Typography>
                          {personal.addressImage ? (
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={async () => {
                                const url = await viewUrl(personal.addressImage);
                                if (url) window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', p: 0 }}
                            >
                              View Full Size
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Not Uploaded</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* PAN Card */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                          {personal.panImage ? (
                            <ImagePreview src={personal.panImageViewUrl || personal.panImage} alt="PAN Card" />
                          ) : (
                            <Avatar sx={{ bgcolor: '#fff3e0', width: 56, height: 56 }}>
                              <DocumentIcon sx={{ color: '#ef6c00' }} />
                            </Avatar>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>PAN Card</Typography>
                          {personal.panImage ? (
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={async () => {
                                const url = await viewUrl(personal.panImage);
                                if (url) window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', p: 0 }}
                            >
                              View Full Size
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Not Uploaded</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Bank Document */}
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box>
                          {/* Bank docs can be PDF, so check extension or just show icon if preview fails/is pdf */}
                          {bankDocSource && (bankDocSource.toLowerCase().endsWith('.pdf') || bankDocSource.includes('.pdf')) ? (
                            <Avatar sx={{ bgcolor: '#f3e5f5', width: 56, height: 56 }}>
                              <PdfIcon sx={{ color: '#7b1fa2' }} />
                            </Avatar>
                          ) : bankDocSource ? (
                            <ImagePreview src={bankDocSource} alt="Bank Doc" />
                          ) : (
                            <Avatar sx={{ bgcolor: '#f3e5f5', width: 56, height: 56 }}>
                              <PdfIcon sx={{ color: '#7b1fa2' }} />
                            </Avatar>
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Bank Document</Typography>
                          {bankDocSource ? (
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={async () => {
                                const url = await viewUrl(bankDocSource);
                                if (url) window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', p: 0 }}
                            >
                              View Document
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Not Uploaded</Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Driving License if exists */}
                    {raw.drivingLicenseImage && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box>
                            <ImagePreview src={raw.drivingLicenseImage} alt="Driving License" />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Driving License</Typography>
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={async () => {
                                const url = await viewUrl(raw.drivingLicenseImage);
                                if (url) window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', p: 0 }}
                            >
                              View Full Size
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    {/* Voter ID if exists */}
                    {raw.voterIdImage && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box>
                            <ImagePreview src={raw.voterIdImage} alt="Voter ID" />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Voter ID Card</Typography>
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              onClick={async () => {
                                const url = await viewUrl(raw.voterIdImage);
                                if (url) window.open(url, '_blank');
                              }}
                              sx={{ textTransform: 'none', p: 0 }}
                            >
                              View Full Size
                            </Button>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Login & Access</Typography>
                  <Grid container spacing={2}>
                    <Item label="Login ID" value={userAccess.loginId} />
                    <Item label="Access Role" value={userAccess.role} />
                    <Item label="Access Active" value={userAccess.isActive ? 'Active' : 'Inactive'} />
                    <Item label="Modules Count" value={modules.length} />
                  </Grid>
                  {modules.length > 0 && (
                    <Box sx={{ mt: 1, pl: 1 }}>
                      {modules.map((m, idx) => (
                        <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace' }}>
                          • {m.name} [{Array.isArray(m.actions) ? m.actions.join(', ') : ''}]
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            variant="contained"
            disableRipple
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Chip (Top level fallback) */}
      {
        error && (
          <Box sx={{ mb: 2 }}>
            <Chip label={error} color="error" />
          </Box>
        )
      }

      {/* Search Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 2,
          transition: 'none !important',
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={10} md={10.5}>
              <Autocomplete
                freeSolo
                options={suggestions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  const name = option.name || 'N/A';
                  const mobile = option.mobile || '';
                  return mobile ? `${name} — ${mobile}` : `${name}`;
                }}
                inputValue={searchInputValue}
                onInputChange={handleSearchInputChange}
                onChange={handleSearchChange}
                filterOptions={(options) => options}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'primary.main',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {option.name?.charAt(0)?.toUpperCase() || 'A'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          color: 'text.primary',
                          mb: 0.3,
                        }}
                        noWrap
                      >
                        {option.name || 'N/A'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: 'text.secondary',
                            }}
                          >
                            {option.mobile || 'N/A'}
                          </Typography>
                        </Box>
                        {option.associatedId && (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                              •
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                              }}
                            >
                              ID: {option.associatedId}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by associate name or number"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
                PaperComponent={({ children, ...other }) => (
                  <Paper
                    {...other}
                    sx={{
                      mt: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      borderRadius: 2,
                    }}
                  >
                    {children}
                  </Paper>
                )}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    paddingRight: '14px !important',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
              <ResetButton onClick={handleReset} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            setPage(0);
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              minHeight: 56,
              transition: 'none !important',
            },
            '& .MuiTabs-indicator': {
              transition: 'none !important',
            },
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label={`All Associates (${totalAssoicatelist || totalDocs})`} />
          <Tab label={`Active (${totalActiveCount})`} />
          <Tab label={`Inactive (${totalInactiveCount})`} />
        </Tabs>
      </Box>

      {/* List */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : data.length === 0 ? (
          <Grid item xs={12}>
            <Box
              sx={{
                py: 10,
                px: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                backgroundColor: 'white',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                No associates found
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Try adjusting your search or filters to find what you're looking for.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Clear all filters
              </Button>
            </Box>
          </Grid>
        ) : data.map((associate) => (
          <Grid item xs={12} key={associate.associatedId}>
            <Card
              sx={{
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'none !important',
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#536DFE',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: 1 }}
                  >
                    Associate ID:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1 }}
                  >
                    {associate.loginId}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                  >
                    Commission:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, fontSize: '16px' }}
                    >
                      {associate.commission} %
                    </Typography>
                  </Box>

                </Box>

                {/* {
                  associate?.totalBookedAmount && associate?.totalBookedAmount > 0 && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                      >
                        Total Business:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, fontSize: '16px' }}
                        >
                          {associate?.totalBookedAmount}
                        </Typography>
                      </Box>
                    </Box>
                  )
                } */}


                {/* {
                  associate?.extraAmount && associate?.extraAmount > 0
                    ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                        >
                          Old Business:
                        </Typography>

                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 700, fontSize: '16px' }}
                          >
                            {associate?.extraAmount}
                          </Typography>
                        </Box>
                      </Box>
                    )
                    : null
                } */}

                {
                  associate?.totalBusiness > 0 && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                      >
                        Total Business:
                      </Typography>

                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, fontSize: '16px' }}
                        >
                          ₹{associate.totalBusiness.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                  )
                }


                {
                  associate?.selfBusiness > 0 && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                      >
                        Self Business:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, fontSize: '16px' }}
                        >
                          ₹{associate.selfBusiness.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                  )
                }

                {/* <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                  >
                    Reward Points:
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, fontSize: '16px' }}
                    >
                      {associate?._raw?.rewaredPoints}
                    </Typography>
                  </Box>
                </Box> */}

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}
                  >
                    Status:
                  </Typography>
                  <Chip
                    label={associate.status}
                    size="small"
                    sx={{
                      backgroundColor:
                        associate.status === 'Active' ? '#4caf50' : '#f44336',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '12px',
                      height: '24px',
                      transition: 'none !important',
                    }}
                    onClick={canWriteAssociate ? () => handleToggleStatus(associate) : undefined}
                    clickable={canWriteAssociate}
                    disableRipple
                  />
                </Box>
              </Box>

              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Chip
                          label={associate.designation}
                          disableRipple
                          sx={{
                            backgroundColor: '#FFC107',
                            color: '#000',
                            fontWeight: 700,
                            fontSize: '11px',
                            height: '22px',
                            transition: 'none !important',
                          }}
                        />
                        <S3Avatar
                          src={associate.image || ''}
                          sx={{
                            width: 80,
                            height: 80,
                            backgroundColor: '#E3F2FD',
                            border: '3px solid #2196F3',
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        >
                          {associate.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {associate.designation}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {associate.city}, {associate.state}
                        </Typography>
                        <Chip
                          label={associate.status}
                          size="small"
                          disableRipple
                          sx={{
                            backgroundColor:
                              associate.status === 'Active'
                                ? '#4caf50'
                                : '#f44336',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '11px',
                            height: '22px',
                            mb: 1,
                            transition: 'none !important',
                          }}
                          onClick={canWriteAssociate ? () => handleToggleStatus(associate) : undefined}
                          clickable={canWriteAssociate}
                        />
                        <Box sx={{ mt: 1.5 }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ mb: 0.5 }}
                          >
                            <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '13px' }}
                            >
                              {associate.email}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{ mb: 0.5 }}
                          >
                            <PhoneIcon
                              sx={{ fontSize: 16, color: '#25D366' }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '13px' }}
                            >
                              {associate.mobile}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationOnIcon
                              sx={{ fontSize: 16, color: '#666' }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '13px' }}
                            >
                              {associate.city}, {associate.state}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Box>
                      <Tabs
                        value={getCardTabValue(associate.associatedId)}
                        onChange={(e, newValue) =>
                          handleCardTabChange(associate.associatedId, newValue)
                        }
                        sx={{
                          minHeight: 40,
                          borderBottom: '1px solid #e0e0e0',
                          '& .MuiTab-root': {
                            minHeight: 40,
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: 600,
                            transition: 'none !important',
                          },
                          '& .MuiTabs-indicator': {
                            transition: 'none !important',
                          },
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                      >
                        <Tab label="Basic Details" />
                        <Tab label="Bank Details" />
                        <Tab label="Sponsor Info" />
                      </Tabs>

                      <Box sx={{ mt: 2 }}>
                        {(() => {
                          const cardTab = getCardTabValue(associate.associatedId);

                          if (cardTab === 0) {
                            const hasBasic = hasValues([
                              associate.pan,
                              associate.aadhar,
                              associate.joiningDate,
                              associate.rank,
                            ]);

                            if (!hasBasic) {
                              return (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textAlign: 'center', py: 3 }}
                                >
                                  No basic details available.
                                </Typography>
                              );
                            }

                            return (
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      PAN Number
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatValue(associate.pan)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      Aadhar Number
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatValue(associate.aadhar)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      Joining Date
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatDateOnly(associate.joiningDate)}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            );
                          }

                          if (cardTab === 1) {
                            const hasBank = hasValues([
                              associate.bankName,
                              associate.accountNumber,
                              associate.ifscCode,
                              associate.branch,
                            ]);

                            if (!hasBank) {
                              return (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textAlign: 'center', py: 3 }}
                                >
                                  No bank details available.
                                </Typography>
                              );
                            }

                            return (
                              <Grid container spacing={1}>
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      Bank Name
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatValue(associate.bankName)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      Account Number
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatValue(associate.accountNumber)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      IFSC Code
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatValue(associate.ifscCode)}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        fontSize: '11px',
                                      }}
                                    >
                                      Branch
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: '12px' }}
                                    >
                                      {formatValue(associate.branch)}
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                            );
                          }

                          const hasSponsor = hasValues([
                            associate.sponsorName,
                            associate.sponsorEmail,
                            associate.sponsorMobile,
                            associate.sponsorId,
                          ]);

                          if (!hasSponsor) {
                            return (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: 'center', py: 3 }}
                              >
                                No sponsor information available.
                              </Typography>
                            );
                          }

                          return (
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '11px',
                                    }}
                                  >
                                    Sponsor Name
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, fontSize: '12px' }}
                                  >
                                    {formatValue(associate.sponsorName)}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '11px',
                                    }}
                                  >
                                    Sponsor Mobile
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, fontSize: '12px' }}
                                  >
                                    {formatValue(associate.sponsorMobile)}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '11px',
                                    }}
                                  >
                                    Sponsor Login ID
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, fontSize: '12px' }}
                                  >

                                    {formatValue(associate.sponsorLoginId || associate.sponsorId)}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1, borderRadius: 1 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '11px',
                                    }}
                                  >
                                    Sponsor Commission
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, fontSize: '12px' }}
                                  >
                                    {formatValue(associate.sponsorCommission)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid #e0e0e0',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewDetails(associate)}
                    disableRipple
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '12px',
                      px: 2,
                    }}
                  >
                    View Details
                  </Button>
                  {associate?.associatedId != 1 && canWriteAssociate && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(associate)}
                        disableRipple
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12px',
                          px: 2,
                        }}
                      >
                        Edit
                      </Button>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDelete(associate)}
                          disableRipple
                          sx={{
                            border: '1px solid #e0e0e0',
                            ml: 0.5,
                            transition: 'none !important',
                          }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}


                  <Tooltip title="View ID Card">
                    <IconButton
                      onClick={() => handleIdCardClick(associate)}
                      disableRipple
                      sx={{
                        border: '1px solid #e0e0e0',
                        ml: 0.5,
                        transition: 'none !important',
                      }}
                    >
                      <CreditCardIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Business Transaction">
                    <IconButton
                      onClick={() => handleViewHistory(associate)}
                      disableRipple
                      sx={{
                        border: '1px solid #e0e0e0',
                        ml: 0.5,
                        transition: 'none !important',

                      }}
                    >
                      <ReceiptLongIcon fontSize="small" color='primary' />
                    </IconButton>
                  </Tooltip>

                  {associate.mobile && (
                    <Tooltip title={`Send Welcome SMS`}>
                      <IconButton
                        onClick={() => handleSendSms(associate)}
                        disableRipple
                        disabled={!!smsLoading[associate._id]}
                        sx={{
                          border: '1px solid #e0e0e0',
                          ml: 0.5,
                          transition: 'none !important',
                        }}
                      >
                        {smsLoading[associate._id]
                          ? <CircularProgress size={16} sx={{ color: '#10b981' }} />
                          : <SmsIcon fontSize="small" sx={{ color: '#10b981' }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {
        totalDocs > 0 && (
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <TablePagination
              // rowsPerPageOptions={[10, 15, 25, 50]}
              component="div"
              count={totalDocs}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} of ${count}`
              }
            />
          </Card>
        )
      }

      {/* PDF Dialog */}
      <PDFDownloadFunctionality
        open={idCardDialogOpen}
        onClose={handleIdCardDialogClose}
        associate={selectedRow}
      />

      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />

      <ConfirmModal
        open={confirmDialog.open}
        title={confirmDialog.title}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={() => {
          if (confirmDialog.action) confirmDialog.action();
        }}
      >
        {confirmDialog.message}
      </ConfirmModal>
    </Box >
  );
};

export default AssociateList;
